import React, { useEffect, useMemo, useState } from "react";
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { Button, Checkbox, Chip, Grid, Stack, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import FeatherIcon from "feather-icons-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import TableComponent from "../../components/table/list.jsx";
import { CustomTooltip, LinearProgressWithLabel } from "../../consts.js";
import Utils from "../../utils";
import ChangeArchiveConfirm from "./change-archive-confirm.jsx";
import { DATE_FORMAT } from "../../helpers/constant.js";
import { format } from "date-fns";
import { useThemeMode } from "../../components/theme/index.jsx";
import { roundNumberHelper } from "../../helpers/index.js";
import { getConversationListAPI } from "../../services/conversation.js";

function CampaignArchived() {

    const navigate = useNavigate();
    const { colours, FONT } = useThemeMode();
    const [openArchiveConfirm, setOpenArchiveConfirm] = React.useState(false);
    const [params] = useSearchParams();
    const [campaignConversationsId] = useState(params.get("id") || "");
    const [data, setData] = useState([]);
    const [spinner, setSpinner] = useState(false);
    const [totalConversations, setTotalConversations] = useState(0);
    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        rowsPerPage: 10,
        nextPage: null,
    });

    async function getCampaignData(rowsPerPage = pageInfo.rowsPerPage, nextPage = pageInfo.nextPage) {
        Utils.sendProgressBar(true);
        setSpinner(true);

        try {
            // api function
            const _data = await getConversationListAPI({
                campaignId: campaignConversationsId,
                archive: true,
                limit: rowsPerPage,
                lastKey: nextPage
            });
            let newData = [..._data.items];
            if (nextPage) {
                newData = [...data, ..._data.items];
            }
            setData([...newData]);
            setPageInfo((prev) => ({ ...prev, nextPage: _data.lastKey || null }));
            setTotalConversations(_data.totalConversations);
            setSpinner(false);
        } catch (error) {
            setSpinner(false);
            console.error("Error fetching campaign data:", error);
        }
    }

    useEffect(() => {
        getCampaignData(); // Your async data fetching function
        window.scrollTo(0, 0); // Scroll to top when component mounts
    }, []);

    /**
     * This method used to reload the page after api success
     */
    const handleUnarchiveSuccess = () => {
        setData([]);
        setOpenArchiveConfirm(false);
        setPageInfo({...pageInfo, nextPage: null, currentPage: 0 });
        getCampaignData(pageInfo.rowsPerPage, null);
    }

    // define table headers
    const columns = useMemo(() => ([
        {
            label: (<Checkbox
                checked={
                    data.length === 0
                        ? false
                        : data
                            .map((q) => q.selected)
                            .filter((q) => q !== true).length === 0 || false
                }
                onClick={(e) => {
                    let d = Object.assign([], data);
                    d.forEach((q) => (q.selected = e.target.checked));
                    setData(d);
                }}
                inputProps={{ 'aria-label': 'Select all rows' }}
            />),
            key: "selected",
            sortable: false,
            renderItem: (row) => (<Checkbox
                checked={row.selected || false}
                onClick={(e) => {
                    e.stopPropagation();
                    const d = Object.assign([], data);
                    const index = d.findIndex((item) => item.id === row.id);
                    d[index].selected = e.target.checked;
                    setData(d);
                }}
                inputProps={{ 'aria-label': `Select ${row.file}` }}
            />)
        },
        {
            label: "File",
            key: "file",
            sortable: false,
            renderItem: (row) => (<Stack direction="row" alignItems="center" gap={2}>
                <FeatherIcon size={20} icon="phone-incoming" style={{ flexShrink: 0 }} />
                {row.file || "-"}
            </Stack>)
        },
        {
            label: "Agent",
            key: "agent",
            sortable: false,
            renderItem: (row) => row.agent || "-",
            style: { minWidth: 150 },
        },
        {
            label: "Call Date",
            key: "createdAt",
            sortable: true,
            renderItem: (row) => (<>
                {format(new Date(row.createdAt), DATE_FORMAT)}
            </>)
        },
        {
            label: "Avg. Call Duration",
            key: "duration",
            sortable: false,
            renderItem: (row) => (row.formatted_duration ? row.formatted_duration : "-")
        },
        {
            label: "Avg. Vocalysd Score",
            key: "symblstatus",
            sortable: false,
            renderItem: (row) => (<>
                {row.symblstatus === "completed" ? (
                    <LinearProgressWithLabel variant="determinate" value={roundNumberHelper(row.VS)} />
                ) : row.symblstatus === "failed" || row.symblstatus === "DEEPGRAM_FAILED" ? (
                    <>
                        <CustomTooltip title={row.message || "Invalid or corrupt audio file"}>
                            <Typography component={"span"}>
                                <FeatherIcon
                                    aria-label="Invalid or corrupt audio file"
                                    icon="alert-triangle"
                                    size="20"
                                    style={{
                                        color: colours.orange100,
                                        flexShrink: 0
                                    }}
                                />
                            </Typography>
                        </CustomTooltip>
                    </>
                ) : (<Typography variant="inherit" display='flex' alignItems='center' gap={1} fontSize={FONT.SIZE_12}><CircularProgress aria-label="Loading status" size={14} color="info" /> Analysis in progress</Typography>)}
            </>)
        },
        {
            label: "# of Alert Hits",
            key: "alertcount",
            sortable: false,
            renderItem: (row) => (<>
                {row.alertcount > 0 ? (
                    <Stack direction="row" alignItems="center" gap={1} sx={{ fontWeight: 500, color: colours.orange100 }}>
                        {row.alertcount}
                        <FeatherIcon
                            aria-label="Alert status"
                            icon="alert-circle"
                            size="20"
                            style={{ flexShrink: 0 }}
                        />
                    </Stack>
                ) : "-"}
            </>)
        }
    ]), [data, colours, FONT]);

    const handlePageChange = (_pageInfo) => {
        let nextPage = pageInfo.nextPage;
        if (_pageInfo.REFRESH_DATA) {
            nextPage = null
        }
        setPageInfo({ 
            ...pageInfo,
            nextPage,
            currentPage: _pageInfo.currentPage,
            rowsPerPage: _pageInfo.rowsPerPage
        });
        if (nextPage || _pageInfo.REFRESH_DATA) {
            getCampaignData(_pageInfo.rowsPerPage, nextPage);
        }
    }

    const headerActionButtons = () => {
        return (data.filter((z) => z.selected === true).length > 0 ? (
            <Grid
                display="flex"
                flexDirection="row"
                flexWrap="wrap"
                justifyContent="end"
                gap={2}
            >
                <Button
                    onClick={() => setOpenArchiveConfirm(true)}
                    variant="outlined"
                    startIcon={<UnarchiveIcon icon="archive" size={20} />}
                    endIcon={<Chip
                        size="small"
                        label={data.filter((z) => z.selected === true).length}
                        sx={{ px: .25 }}
                    />}
                    sx={{
                        '.MuiButton-endIcon .MuiChip-root': {
                            fontSize: FONT.SIZE_12,
                            fontWeight: 500
                        }
                    }}
                >
                    Unarchive
                </Button>
            </Grid>
        ) : null
        )
    }

    return (
        <Grid display="flex" flexDirection="column" flexGrow="1" overflow="auto">
            <Grid
                className="vocalysed-home-upadted campaigns"
                container
                spacing={0}
                direction="column"
                flexWrap="nowrap"
                flexGrow={1}
                paddingTop={2}
                paddingX={3}
                paddingBottom={3}
                overflow="auto"
            >
                <TableComponent
                    headerActionButtons={headerActionButtons}
                    spinner={spinner}
                    data={data}
                    columns={columns}
                    pagination={true}
                    serverPaginate={true}
                    currentPage={pageInfo.currentPage}
                    hasNextPage={pageInfo.nextPage !== null}
                    totalCount={totalConversations}
                    noDataMessage={"No conversation found."}
                    searchKeys={["file", "agent"]}
                    defaultOptions={{ sortKey: 'createdAt', sortOrder: 'desc', rowPerPage: pageInfo.rowsPerPage }}
                    onPageChange={handlePageChange}
                    rowClick={(item) => {
                        if (item.symblstatus === "completed") {
                            navigate("/conversation?id=" + item.id);
                        }
                    }}
                />
            </Grid>

            {/* change to unarchive confirm popup */}
            <ChangeArchiveConfirm
                setArchiveState={false} // false to unarchive the conversation 
                open={openArchiveConfirm}
                data={data}
                onClose={() => setOpenArchiveConfirm(false)}
                onSuccess={handleUnarchiveSuccess} />
        </Grid>
    );
}

export default CampaignArchived;
